import { ReversedMediaSource, MediaSource } from '@src/types/Filters';
import { UserWithFeaturesView } from '@src/types/common';
import { SourceNetwork } from '@src/types/SourceNetwork';
import { MentionPageView } from 'common';

const SEPARATOR = '_';
const SEP_REGEXP = new RegExp(`^(\\w+?)${SEPARATOR}(\\w+?)${SEPARATOR}(.+)`);
export type CustomSourceVariant = 'customer' | 'visitor';
export const CUSTOM_VARIANT_LABELS: Record<CustomSourceVariant, string> = {
  customer: 'Brand asset',
  visitor: 'Visitor upload',
};

const TYPES = { hashtag: 1, mention: 1, tagged: 1, ownContent: 1, extension: 1, custom: 1 }; // Define available type of sources
export type SourceType = (keyof typeof TYPES);
export interface DetailedSource {
  type: (keyof typeof TYPES)
  network: SourceNetwork
  value: string
  other?: string // Used to keep mention page id
  variant?: CustomSourceVariant
}
export type SourceUrlKey = string;

export class SourceUtility {
  /**
     * Convert DetailedSource to a key as string for the url parameter
     */
  public static sourceToUrlKey(source: DetailedSource): SourceUrlKey {
    const keyParts = [
      MediaSource[source.network],
      source.type,
      source.value,
    ];
    if (source.variant) {
      keyParts.push(source.variant);
    }
    return keyParts.join(SEPARATOR);
  }

  /**
     * Convert key from url to DetailedSource
     */
  public static urlKeyToSource(key: SourceUrlKey): DetailedSource | null {
    const customVariantMatch = /^custom_custom_custom_(customer|visitor)$/.exec(key);
    if (customVariantMatch) {
      const variant = customVariantMatch[1] as CustomSourceVariant;
      return {
        network: 'custom',
        type: 'custom',
        value: 'custom',
        variant,
      };
    }

    const k = SEP_REGEXP.exec(key);
    if (!k) {
      return null;
    }
    const abridgedNetwork = k[1] as 'ig' | 'tiktok' | 'custom';
    const type = k[2] as 'hashtag' | 'mention' | 'tagged' | 'ownContent' | 'extension' | 'custom';
    const value: string = k[3];

    const network = ReversedMediaSource[abridgedNetwork] as SourceNetwork;
    if (!TYPES[type] || !network) {
      return null;
    }
    return {
      network,
      type,
      value,
    };
  }

  public static isCustomVariantSource(source: DetailedSource, variant?: CustomSourceVariant): boolean {
    return source.network === 'custom' && source.type === 'custom' && source.variant === variant;
  }

  public static toDisplayLabel(source: DetailedSource): string {
    if (source.network === 'custom' && source.type === 'custom') {
      if (source.variant) {
        return CUSTOM_VARIANT_LABELS[source.variant];
      }
      return 'Custom';
    }
    return source.value;
  }

  /**
     * Return data from the store formatted as DetailedSources
     */
  public static getAllDetailedSources({
    hashtags = [],
    mentionPages = {},
  }: {
    hashtags?: string[]
    mentionPages?: { [key: string]: MentionPageView }
  }): DetailedSource[] {
    const sources: DetailedSource[] = [];
    Object.values(mentionPages).forEach((mention) => {
      if (!MediaSource[mention.source]) {
        return;
      }
      sources.push({
        network: mention.source,
        type: 'mention',
        value: mention.name,
        other: mention.id,
      });
      if (mention.source === 'instagram') {
        sources.push({
          network: mention.source as SourceNetwork,
          type: 'tagged',
          value: mention.name,
          other: mention.id,
        });

        sources.push({
          network: mention.source as SourceNetwork,
          type: 'ownContent',
          value: mention.name,
          other: mention.id,
        })
      }
    });
    hashtags.forEach((hashtag) => {
        const h: Pick<DetailedSource, 'type' | 'value'> = { type: 'hashtag', value: hashtag };
        sources.push({ network: 'instagram', ...h });
    });
    sources.push({
      network: 'tiktok',
      type: 'extension',
      value: 'tiktok',
      other: 'tiktok',
    });
    sources.push({
      network: 'custom',
      type: 'custom',
      value: 'custom',
      other: 'custom',
    });
    (Object.keys(CUSTOM_VARIANT_LABELS) as CustomSourceVariant[]).forEach((variant) => {
      sources.push({
        network: 'custom',
        type: 'custom',
        value: 'custom',
        other: variant,
        variant,
      });
    });

    return sources;
  }

  /**
     * Return data from the store formatted as DetailedSources
     */
  public static getAllDetailedSourcesFromState(common: { user: UserWithFeaturesView | null; groupMentionPages: Record<string, MentionPageView> }): DetailedSource[] {
    return SourceUtility.getAllDetailedSources({
      hashtags: common.user?.group.tags,
      mentionPages: common.groupMentionPages,
    });
  }
}
